import type { Node, Edge } from '../../../core/models/scene';

// --- FocusScope discriminated union ---

export type FocusScope =
  | { readonly type: 'all' }
  | { readonly type: 'department'; readonly departmentName: string }
  | { readonly type: 'subtree'; readonly rootPersonId: string };

export const DEFAULT_FOCUS_SCOPE: FocusScope = { type: 'all' } as const;

// --- Dot grid lightweight data ---

export interface DotGridPerson {
  readonly id: string;
  readonly name?: string;
  readonly colorValue: unknown;
}

// --- Boundary node (collapsed subtree placeholder) ---

export interface BoundaryNode {
  readonly parentId: string;
  readonly hiddenCount: number;
  readonly label: string;
  readonly expandScope: FocusScope;
  readonly dotData: readonly DotGridPerson[];
}

// --- Projection result ---

export interface FocusProjectionResult {
  readonly focusNodes: readonly Node[];
  readonly focusEdges: readonly Edge[];
  readonly anchorChainNodes: readonly Node[];
  readonly anchorChainEdges: readonly Edge[];
  readonly boundaryNodes: readonly BoundaryNode[];
  readonly anchorChainIncomplete: boolean;
}

export const EMPTY_FOCUS_PROJECTION: FocusProjectionResult = {
  focusNodes: [],
  focusEdges: [],
  anchorChainNodes: [],
  anchorChainEdges: [],
  boundaryNodes: [],
  anchorChainIncomplete: false,
};

// --- Anchor chain edge helpers ---

interface AnchorChainEdgeAttrs {
  readonly anchorChain: true;
}

export const ANCHOR_NODE_TYPE = 'org_person_anchor' as const;

/** Check if a node is an anchor chain node (marked via attrs.anchorChain) */
export function isAnchorChainNode(node: Node): boolean {
  return (node.attrs as Record<string, unknown>)?.anchorChain === true;
}

export function createAnchorChainEdge(from: string, to: string): Edge {
  return {
    id: `anchor-edge-${from}-${to}`,
    type: 'reporting',
    from,
    to,
    attrs: { anchorChain: true } satisfies AnchorChainEdgeAttrs,
  };
}

export function isAnchorChainEdge(edge: Edge): boolean {
  return edge.attrs !== undefined
    && 'anchorChain' in edge.attrs
    && edge.attrs.anchorChain === true;
}
